import { FACE_CONFIG, FACE_ORDER } from '../core/cube-config.js';
import { applyAlgorithmToState4x4 } from '../core/moves-4x4.js';
import { deriveOrientation4x4 } from '../core/orientation-4x4.js';
import { isSolved4x4, stickersToSolverFacelets4x4 } from '../core/validation-4x4.js';
import { validateCubeState } from '../core/validation.js';
import { SolverClient, SolverError } from './solver-client.js';
import { SolverExecutor } from './solver-executor.js';
import solverWorker4x4Url from './solver-worker-4x4.js?worker&url';
import {
  buildSolverInputDiagnostic4x4,
  createSolverDiagnostic4x4,
  SOLVER_DIAGNOSTIC_STAGE_4X4,
} from './solver-diagnostics-4x4.js';

export const SOLVER_STATUS_4X4 = Object.freeze({
  INITIALIZING: 'initializing',
  SOLVING: 'solving',
  COMPATIBILITY: 'compatibility',
  VERIFYING: 'verifying',
  COMPLETE: 'complete',
});

export const SOLVER_STATUS_TEXT_4X4 = Object.freeze({
  [SOLVER_STATUS_4X4.INITIALIZING]: '正在初始化 4×4 求解器',
  [SOLVER_STATUS_4X4.SOLVING]: '正在计算完整方案',
  [SOLVER_STATUS_4X4.COMPATIBILITY]: '兼容模式，求解期间页面可能短暂卡顿',
  [SOLVER_STATUS_4X4.VERIFYING]: '正在重放并验证 96 张贴纸',
  [SOLVER_STATUS_4X4.COMPLETE]: '求解完成',
});

export const THREEPHASE_MOVE_SYMBOLS = Object.freeze([
  'U', 'U2', "U'", 'R', 'R2', "R'", 'F', 'F2', "F'",
  'D', 'D2', "D'", 'L', 'L2', "L'", 'B', 'B2', "B'",
  'Uw', 'Uw2', "Uw'", 'Rw', 'Rw2', "Rw'", 'Fw', 'Fw2', "Fw'",
  'Dw', 'Dw2', "Dw'", 'Lw', 'Lw2', "Lw'", 'Bw', 'Bw2', "Bw'",
]);

const THREE_BY_THREE_SAMPLE = Object.freeze([0, 1, 3, 4, 5, 7, 12, 13, 15]);
const CUBE_ROTATIONS = Object.freeze([
  Object.freeze(['Rw', "Lw'"]), Object.freeze(["Rw'", 'Lw']),
  Object.freeze(['Uw', "Dw'"]), Object.freeze(["Uw'", 'Dw']),
  Object.freeze(['Fw', "Bw'"]), Object.freeze(["Fw'", 'Bw']),
]);

function now() {
  return globalThis.performance?.now() ?? Date.now();
}

function noopStatus(_status) {}
function noopDiagnostic(_event) {}

function nextPaint() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => resolve());
    else setTimeout(resolve, 0);
  });
}

function hasExpectedCenters(stickers, orientation) {
  return FACE_ORDER.every((face) => stickers[face][5] === orientation.faceToColor[face]);
}

export function findOrientationMoves4x4(stickers, rawOrientation) {
  const orientation = rawOrientation.faceToColor ? rawOrientation : deriveOrientation4x4(rawOrientation);
  const queue = [{ stickers, moves: [] }];
  const visited = new Set();
  while (queue.length) {
    const current = queue.shift();
    const signature = FACE_ORDER.map((face) => current.stickers[face][5]).join(',');
    if (visited.has(signature)) continue;
    visited.add(signature);
    if (hasExpectedCenters(current.stickers, orientation)) return current.moves;
    if (current.moves.length >= 6) continue;
    for (const rotation of CUBE_ROTATIONS) {
      queue.push({
        stickers: applyAlgorithmToState4x4(current.stickers, rotation),
        moves: [...current.moves, ...rotation],
      });
    }
  }
  throw new SolverError('CENTER_ORIENTATION_MISMATCH', '4×4 降阶后的中心方向无法规范化，已拒绝继续求解');
}

function toCanonicalThreeByThree(stickers, orientation) {
  return Object.fromEntries(FACE_ORDER.map((face) => [
    face,
    THREE_BY_THREE_SAMPLE.map((index) => {
      const actualColor = stickers[face][index];
      const logicalFace = orientation.colorToFace[actualColor];
      return FACE_CONFIG[logicalFace].color;
    }),
  ]));
}

function equalArrays(a, b) {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

export class SolverClient4x4 {
  constructor({
    workerFactory = () => new Worker(solverWorker4x4Url, { type: 'module' }),
    solver3x3 = new SolverClient({ timeoutMs: 90000 }),
    timeoutMs = 120000,
    readyTimeoutMs = 5000,
    executor = null,
  } = {}) {
    this.solver3x3 = solver3x3;
    this.timeoutMs = timeoutMs;
    this.activeCancellation = null;
    this.workerUrl = new URL(solverWorker4x4Url, import.meta.url);
    this.executor = executor ?? new SolverExecutor({
      label: '4×4',
      workerFactory,
      workerUrl: this.workerUrl.href,
      workerType: 'module',
      readyTimeoutMs,
      timeoutMs,
      fallback: (payload, hooks) => this.#reduceOnMainThread(payload, hooks),
    });
  }

  async #reduceOnMainThread({ facelets }, { onStatus, onDiagnostic, startedAtMs }) {
    await nextPaint();
    onStatus(SOLVER_STATUS_4X4.COMPATIBILITY);
    const emit = (stage, details = {}) => onDiagnostic(createSolverDiagnostic4x4(stage, {
      source: 'main-fallback-4x4',
      startedAtMs,
      details,
    }));
    emit(SOLVER_DIAGNOSTIC_STAGE_4X4.DEPENDENCY_LOAD_START, {
      module: 'threephase-adapter',
      execution: 'main-thread',
    });
    const [{ analyzeSolverFacelets4x4 }, adapter] = await Promise.all([
      import('../core/solver-facelets-4x4.js'),
      import('../vendor/threephase-adapter.js'),
    ]);
    emit(SOLVER_DIAGNOSTIC_STAGE_4X4.DEPENDENCY_LOAD_END, {
      module: 'threephase-adapter',
      execution: 'main-thread',
    });
    emit(SOLVER_DIAGNOSTIC_STAGE_4X4.WASM_LOAD_START, { required: false });
    emit(SOLVER_DIAGNOSTIC_STAGE_4X4.WASM_LOAD_END, {
      required: false,
      implementation: 'javascript-threephase',
    });
    const analysis = analyzeSolverFacelets4x4(facelets);
    if (!analysis.valid) {
      throw Object.assign(new TypeError(`4×4求解器输入非法：${analysis.errors[0].message}`), {
        code: 'INVALID_SOLVER_FACELETS',
      });
    }
    onStatus(SOLVER_STATUS_4X4.INITIALIZING);
    emit(SOLVER_DIAGNOSTIC_STAGE_4X4.SOLVER_INIT_START, { execution: 'main-thread' });
    const initializationStartedAt = now();
    await adapter.initialize();
    const initializationDurationMs = Math.round(now() - initializationStartedAt);
    emit(SOLVER_DIAGNOSTIC_STAGE_4X4.SOLVER_INIT_END, {
      execution: 'main-thread',
      durationMs: initializationDurationMs,
    });
    onStatus(SOLVER_STATUS_4X4.SOLVING);
    const centersSolved = analysis.centers.every((center) => center.face === center.logicalFace);
    const solved = facelets.every((color, index) => color === Math.floor(index / 16));
    const preconditioningMoveIndices = centersSolved && !solved && !analysis.threephasePhase2Ready ? [21] : [];
    const solverInputFacelets = preconditioningMoveIndices.length
      ? adapter.apply444Moves(facelets, preconditioningMoveIndices)
      : facelets;
    emit(SOLVER_DIAGNOSTIC_STAGE_4X4.SOLVE_START, {
      execution: 'main-thread',
      solver: 'threephase',
    });
    const solveStartedAt = now();
    const reduction = adapter.solve444Facelets(solverInputFacelets, {
      onProgress: ({ stage, ...details }) => emit(`threephase-${stage}`, details),
    });
    const rawReductionMoves = [...preconditioningMoveIndices, ...reduction.rawReductionMoves];
    const solveDurationMs = Math.round(now() - solveStartedAt);
    emit(SOLVER_DIAGNOSTIC_STAGE_4X4.SOLVE_END, {
      execution: 'main-thread',
      solver: 'threephase',
      durationMs: solveDurationMs,
    });
    return {
      ...reduction,
      reductionFormula: `${preconditioningMoveIndices.length ? 'Rw ' : ''}${reduction.reductionFormula}`.trim(),
      rawReductionMoves,
      preconditioningMoveIndices,
      reductionDurationMs: solveDurationMs,
      threephaseInitializationDurationMs: initializationDurationMs,
      threephaseSolveDurationMs: solveDurationMs,
      workerDurationMs: 0,
    };
  }

  #reduce(facelets, onStatus, emitDiagnostic, startedAtMs) {
    return this.executor.execute({ facelets }, {
      onStatus,
      onDiagnostic: (event) => emitDiagnostic(event),
      onCompatibilityMode: () => onStatus(SOLVER_STATUS_4X4.COMPATIBILITY),
      startedAtMs,
    }).catch((cause) => {
      if (cause instanceof SolverError) throw cause;
      const error = new SolverError(
        cause?.code ?? 'WORKER_ERROR',
        `4×4求解未完成${cause?.workerStage ? `，阶段：${cause.workerStage}` : ''}。${cause?.name ?? 'Error'}: ${cause?.message ?? cause}`,
        cause,
      );
      error.workerStage = cause?.workerStage ?? null;
      error.workerStack = cause?.workerStack ?? cause?.stack ?? '';
      error.resourceUrl = cause?.resourceUrl ?? this.workerUrl.href;
      throw error;
    });
  }

  cancel(reason = '用户已取消 4×4 求解') {
    if (!this.activeCancellation) return false;
    const cancellation = this.activeCancellation;
    this.activeCancellation = null;
    this.executor.cancel(reason);
    cancellation.reject(new SolverError('CANCELED', reason));
    return true;
  }

  async solve(stickers, rawOrientation, {
    onStatus = noopStatus,
    onDiagnostic = noopDiagnostic,
    startedAtMs = Date.now(),
  } = {}) {
    if (this.activeCancellation) throw new SolverError('BUSY', '已有一个 4×4 求解任务正在运行');
    const diagnostics = [];
    const emitDiagnostic = (stageOrEvent, details = {}) => {
      const event = typeof stageOrEvent === 'string'
        ? createSolverDiagnostic4x4(stageOrEvent, { source: 'main', startedAtMs, details })
        : stageOrEvent;
      diagnostics.push(event);
      onDiagnostic(event);
      return event;
    };
    const inputStickers = structuredClone(stickers);
    emitDiagnostic(SOLVER_DIAGNOSTIC_STAGE_4X4.STICKERS_READY, {
      stickerCount: FACE_ORDER.reduce((sum, face) => sum + (inputStickers[face]?.length ?? 0), 0),
      faceOrder: FACE_ORDER.join(''),
    });
    const orientation = rawOrientation.faceToColor ? rawOrientation : deriveOrientation4x4(rawOrientation);
    const solverFacelets = stickersToSolverFacelets4x4(inputStickers, orientation);
    const inputDiagnostics = buildSolverInputDiagnostic4x4(inputStickers, orientation, solverFacelets);
    emitDiagnostic(SOLVER_DIAGNOSTIC_STAGE_4X4.ORIENTATION_READY, inputDiagnostics);
    const startedAt = now();
    let cancelReject = (_error) => {};
    const cancellationPromise = new Promise((_, reject) => { cancelReject = reject; });
    this.activeCancellation = { reject: cancelReject };

    const task = (async () => {
      const reduction = await this.#reduce(solverFacelets, onStatus, emitDiagnostic, startedAtMs);
      const reductionMoves = reduction.rawReductionMoves.map((index) => THREEPHASE_MOVE_SYMBOLS[index]);
      if (reductionMoves.some((move) => !move)) {
        throw new SolverError('UNKNOWN_MOVE', '4×4 求解器返回了无法识别的内部动作');
      }

      emitDiagnostic(SOLVER_DIAGNOSTIC_STAGE_4X4.CONVERT_3X3_START, {
        reductionMoveCount: reductionMoves.length,
      });
      const conversionStartedAt = now();
      const reducedStickers = applyAlgorithmToState4x4(inputStickers, reductionMoves);
      const replayedFacelets = stickersToSolverFacelets4x4(reducedStickers, orientation);
      if (!equalArrays(replayedFacelets, reduction.reducedFacelets)) {
        throw new SolverError('REDUCTION_REPLAY_MISMATCH', '4×4 降阶动作与求解器内部状态不一致，已拒绝继续');
      }

      const orientationMoves = findOrientationMoves4x4(reducedStickers, orientation);
      const normalizedStickers = applyAlgorithmToState4x4(reducedStickers, orientationMoves);
      const validation3x3 = validateCubeState(toCanonicalThreeByThree(normalizedStickers, orientation));
      if (!validation3x3.valid) {
        throw new SolverError('REDUCTION_INVALID', `4×4 降阶结果无法转换为合法三阶状态：${validation3x3.errors[0].message}`);
      }
      const conversionDurationMs = Math.round(now() - conversionStartedAt);
      emitDiagnostic(SOLVER_DIAGNOSTIC_STAGE_4X4.CONVERT_3X3_END, {
        durationMs: conversionDurationMs,
        orientationMoves,
      });

      emitDiagnostic(SOLVER_DIAGNOSTIC_STAGE_4X4.MIN2PHASE_START);
      const min2phaseStartedAt = now();
      const finish3x3 = await this.solver3x3.solve(validation3x3.patternData, {
        onStatus: (status) => {
          if (status === 'initializing') onStatus(SOLVER_STATUS_4X4.INITIALIZING);
          else if (status === 'solving') onStatus(SOLVER_STATUS_4X4.SOLVING);
        },
      });
      const min2phaseDurationMs = Math.round(now() - min2phaseStartedAt);
      emitDiagnostic(SOLVER_DIAGNOSTIC_STAGE_4X4.MIN2PHASE_END, {
        durationMs: min2phaseDurationMs,
        moveCount: finish3x3.moveCount,
      });
      onStatus(SOLVER_STATUS_4X4.VERIFYING);
      const moves = [...reductionMoves, ...orientationMoves, ...finish3x3.moves];
      emitDiagnostic(SOLVER_DIAGNOSTIC_STAGE_4X4.REPLAY_START, { moveCount: moves.length });
      const replayStartedAt = now();
      const finalStickers = applyAlgorithmToState4x4(inputStickers, moves);
      if (!isSolved4x4(finalStickers, orientation)) {
        throw new SolverError('RESULT_MISMATCH', '完整 4×4 公式重放后未能复原 96 张贴纸，已拒绝进入动画');
      }
      const replayDurationMs = Math.round(now() - replayStartedAt);
      emitDiagnostic(SOLVER_DIAGNOSTIC_STAGE_4X4.REPLAY_END, {
        durationMs: replayDurationMs,
        verifiedStickerCount: 96,
      });
      onStatus(SOLVER_STATUS_4X4.COMPLETE);
      return {
        formula: moves.join(' '),
        moves,
        moveCount: moves.length,
        durationMs: Math.round(now() - startedAt),
        reductionDurationMs: reduction.reductionDurationMs,
        threephaseInitializationDurationMs: reduction.threephaseInitializationDurationMs,
        threephaseSolveDurationMs: reduction.threephaseSolveDurationMs,
        workerDurationMs: reduction.workerDurationMs,
        conversionDurationMs,
        min2phaseDurationMs,
        replayDurationMs,
        phaseSearchDepths: reduction.phases,
        reductionMoveCount: reductionMoves.length + orientationMoves.length,
        threeByThreeMoveCount: finish3x3.moveCount,
        orientationMoveCount: orientationMoves.length,
        verified: true,
        verifiedStickerCount: 96,
        compatibilityMode: Boolean(reduction.compatibilityMode || finish3x3.compatibilityMode),
        workerError: reduction.workerError ?? finish3x3.workerError ?? null,
        inputDiagnostics,
        diagnostics,
      };
    })();

    const timer = setTimeout(() => {
      emitDiagnostic(SOLVER_DIAGNOSTIC_STAGE_4X4.TIMEOUT, { timeoutMs: this.timeoutMs });
      this.cancel(`4×4 求解超过 ${Math.round(this.timeoutMs / 1000)} 秒，已停止`);
    }, this.timeoutMs);
    try {
      return await Promise.race([task, cancellationPromise]);
    } catch (cause) {
      cause.diagnostics = [...diagnostics];
      cause.inputDiagnostics = inputDiagnostics;
      throw cause;
    } finally {
      clearTimeout(timer);
      this.activeCancellation = null;
    }
  }

  dispose() {
    if (this.activeCancellation) this.cancel('4×4 求解器已关闭');
    this.executor.dispose();
  }
}
